/**
 * The document library (S-A47).
 *
 * Runs against the real `DocumentViewSet`. Scope comes from the kernel: a document is
 * in reach when its case is assigned to the actor, or it is client-level for a client
 * already in scope. Documents legitimately exist before a case does (CR-03), which is
 * why this list is not nested under a matter.
 */

import { qk, usePaginatedList, type DocumentListItem } from '@legalcrm/api';
import { Input, Select } from '@legalcrm/ui';
import { useEffect, useState } from 'react';

import { STAFF } from '@legalcrm/api';
import { PageHeader } from '@/shell/PageHeader';

import { DocumentsTable } from './DocumentsTable';
import { ReviewPanel } from './DocumentReviewPage';

const STATUS_OPTIONS = [
  { value: '', label: 'Any review status' },
  { value: 'pending', label: 'Pending' },
  { value: 'under_review', label: 'Under review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const VISIBILITY_OPTIONS = [
  { value: '', label: 'Any visibility' },
  { value: 'internal', label: 'Internal only' },
  { value: 'client', label: 'Client can see' },
];

export function DocumentListPage() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [visibility, setVisibility] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const list = usePaginatedList<DocumentListItem>(qk.list('documents'), STAFF.documents, {
    search: debounced || undefined,
    status: status || undefined,
    visibility: visibility || undefined,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [debounced, status, visibility, resetCursor]);

  return (
    <>
      <PageHeader
        title="Documents"
        description="Everything collected across the matters you can see."
      />

      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <Input
          label="Search"
          hideLabel
          type="search"
          placeholder="Search by name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select
          label="Review status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
        <Select
          label="Visibility"
          hideLabel
          options={VISIBILITY_OPTIONS}
          value={visibility}
          onChange={(event) => setVisibility(event.target.value)}
        />
      </div>

      {/* The same panel the review queue opens. A document you can see but cannot
          open is a dead end: there was no way to re-read a decision, share a file
          with a client, or download one, except by finding it in the queue -- which
          only ever holds what is still undecided. */}
      {openId && <ReviewPanel documentId={openId} onClose={() => setOpenId(null)} />}

      <DocumentsTable
        onRowClick={(row) => setOpenId(row.id)}
        rows={list.items}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />
    </>
  );
}
